import type { AsyncMutex } from '../core/async-mutex.js'
import type { DatabaseDriver, SqlValue } from '../core/driver.js'

/**
 * Транзакция как отдельный объект.
 *
 * База за драйвером — одно соединение, а транзакция в SQLite — состояние этого
 * соединения. Пока она открыта, **любой** запрос, ушедший в то же соединение,
 * выполняется внутри неё: откат уносит с собой и чужую запись, сделанную рядом.
 * Мьютексом на каждый вызов это не лечится — «своя» операция от чужой в момент
 * вызова неотличима.
 *
 * Поэтому граница проводится по объекту, а не по времени: наружу драйвер
 * ставит все операции в очередь, а внутрь колбэка отдаёт эту обёртку, которая
 * ходит в соединение напрямую. Очередь на время транзакции занята ею целиком,
 * так что соседний `execute` дождётся коммита и не окажется под чужим откатом.
 *
 * Цена — обратная сторона той же монеты: обращение к самому драйверу изнутри
 * колбэка встанет в очередь за транзакцией, которая ждёт его же завершения.
 * Поэтому внутри транзакции работают **только** через переданный `tx`.
 */

/** Операции драйвера в обход очереди — то, чем транзакция пользуется изнутри. */
export interface RawOperations {
  execute(sql: string, params?: SqlValue[]): Promise<void>
  select<Row>(sql: string, params?: SqlValue[]): Promise<Row[]>
  selectBlobs?(sql: string, params?: SqlValue[]): Promise<Uint8Array[]>
}

/** Границы транзакции: у драйверов это SQL, у прокси — сообщения соседу. */
export interface TransactionBoundary {
  begin(): Promise<void>
  commit(): Promise<void>
  rollback(): Promise<void>
}

/**
 * Обёртка живёт ровно столько, сколько открыта транзакция.
 *
 * После выхода из колбэка она обязана перестать работать: код, забывший
 * дождаться своего `await`, писал бы уже мимо транзакции — в чужую или вовсе
 * вне очереди. Внятная ошибка здесь лучше молчаливой записи не туда.
 */
function openScope(raw: RawOperations): { tx: DatabaseDriver; finish: () => void } {
  let open = true

  const guard = (): void => {
    if (!open) throw new Error('транзакция уже закрыта')
  }

  const tx: DatabaseDriver = {
    async execute(sql: string, params?: SqlValue[]): Promise<void> {
      guard()
      return raw.execute(sql, params)
    },
    async select<Row>(sql: string, params?: SqlValue[]): Promise<Row[]> {
      guard()
      return raw.select<Row>(sql, params)
    },
    // Вложенный вызов идёт в уже открытой транзакции: SQLite не знает вложенных
    // BEGIN, а доменным сервисам удобно не думать, кто их позвал.
    transaction<Result>(run: (nested: DatabaseDriver) => Promise<Result>): Promise<Result> {
      guard()
      return run(tx)
    },
    async close(): Promise<void> {
      throw new Error('нельзя закрывать базу изнутри транзакции')
    },
  }

  // Быстрый путь к блобам есть не у всех драйверов, и вызывающий проверяет это
  // наличием метода: объявить его пустышкой значило бы соврать (см. LeaderHost).
  if (raw.selectBlobs) {
    const selectBlobs = raw.selectBlobs.bind(raw)
    tx.selectBlobs = async (sql: string, params?: SqlValue[]): Promise<Uint8Array[]> => {
      guard()
      return selectBlobs(sql, params)
    }
  }

  return { tx, finish: () => (open = false) }
}

/**
 * Выполнить колбэк в транзакции, заняв очередь драйвера на всё её время.
 *
 * Общая для всех драйверов часть: различаются они только тем, чем открывают и
 * закрывают транзакцию и как ходят в базу.
 */
export function runInTransaction<Result>(options: {
  mutex: AsyncMutex
  raw: RawOperations
  boundary: TransactionBoundary
  run: (tx: DatabaseDriver) => Promise<Result>
}): Promise<Result> {
  const { mutex, raw, boundary, run } = options

  return mutex.run(async () => {
    const scope = openScope(raw)
    await boundary.begin()
    try {
      const result = await run(scope.tx)
      await boundary.commit()
      return result
    } catch (error) {
      // Ошибку отката глотаем: наверх должна уйти та, из-за которой откатывались.
      await boundary.rollback().catch(() => undefined)
      throw error
    } finally {
      scope.finish()
    }
  })
}
